import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { LoginInput, LoginResponse, SessionUser } from '@/shared/types';
import { api, ApiError } from '@/lib/api';
import { useSession } from '@/stores/session';

export function useBootstrapSession(): { isLoading: boolean } {
  const { setUser, setHydrated, hydrated } = useSession();
  const query = useQuery({
    queryKey: ['session', 'me'],
    queryFn: () => api<{ user: SessionUser }>('/auth/me'),
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (query.isSuccess) setUser(query.data.user);
    if (query.isError) {
      const err = query.error as ApiError;
      if (err.status === 401) setUser(null);
    }
    if ((query.isSuccess || query.isError) && !hydrated) setHydrated(true);
  }, [query.isSuccess, query.isError, query.data, query.error, setUser, setHydrated, hydrated]);

  return { isLoading: !hydrated };
}

export function useLogin() {
  const setUser = useSession((s) => s.setUser);
  return useMutation({
    mutationFn: (input: LoginInput) =>
      api<LoginResponse>('/auth/login', { method: 'POST', body: input }),
    onSuccess: (res) => setUser(res.user),
  });
}

export function useLogout() {
  const reset = useSession((s) => s.reset);
  return useMutation({
    mutationFn: () => api<{ ok: true }>('/auth/logout', { method: 'POST' }),
    onSettled: () => reset(),
  });
}
